import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { resetBodyScrollLock } from "@/lib/capacitorScrollLock";
import { useQuickActionNavigator } from "@/hooks/useQuickActionNavigator";
import {
  forceResetScroll,
  logScrollSnapshot,
  isScrollDebugEnabled,
} from "@/lib/scrollContainer";

/** Force browser to repaint — fixes WKWebView/Safari blank-after-navigate bug */
const forceRepaint = () => {
  const root = document.querySelector('.app-root') as HTMLElement | null;
  const target = root || document.body;
  target.style.transform = 'translateZ(0)';
  void target.offsetHeight;
  target.style.transform = '';
};

const runScrollReset = (pathname: string) => {
  const debug = isScrollDebugEnabled();

  // Reset scroll-lock state first so stale restores cannot re-apply old scroll
  resetBodyScrollLock();

  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  if (debug) logScrollSnapshot(`BEFORE reset (${pathname})`);

  forceResetScroll();

  if (debug) logScrollSnapshot(`AFTER reset (${pathname})`);

  forceRepaint();

  // Safety net: re-check at intervals to catch any late effect restoring scroll
  const delays = [50, 100, 200, 400, 700];
  const timers = delays.map((delay) =>
    window.setTimeout(() => {
      forceResetScroll();
      forceRepaint();
      if (debug) logScrollSnapshot(`+${delay}ms (${pathname})`);
    }, delay)
  );

  return () => {
    timers.forEach(clearTimeout);
  };
};

export const ScrollToTop = () => {
  const { pathname, search, hash, key } = useLocation();

  // Handle iOS home screen quick action deep-links
  useQuickActionNavigator();

  // Reset scroll on every route/history entry change
  useLayoutEffect(() => {
    // Skip scroll-to-top when navigating to a hash — the page's
    // useHashSectionNavigation hook handles scrolling to the target.
    if (hash) return;
    // Skip when a page (currently /checkout) has signaled it is restoring
    // its own scroll position from persisted state. The flag is set
    // synchronously by the page mount and cleared after restoration.
    if (document.documentElement.dataset.checkoutRestoring === '1') return;
    // One-shot opt-out: a page sets this flag immediately before an
    // in-place query/navigation update (e.g. date change on hotel-details)
    // to keep the user's scroll position. The flag is consumed and cleared
    // here so it only affects the very next navigation.
    if (document.documentElement.dataset.suppressScrollReset === '1') {
      delete document.documentElement.dataset.suppressScrollReset;
      return;
    }
    return runScrollReset(pathname);
  }, [pathname, search, hash, key]);

  // Also listen to popstate for browser/native back button edge cases
  useEffect(() => {
    const handlePopState = () => {
      const cleanup = runScrollReset(pathname);
      window.setTimeout(cleanup, 800);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [pathname]);

  return null;
};
